import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import {
    fetchAdminroles,
    createAdminrole,
    updateAdminrole,
    deleteAdminrole,
    downloadAdminrole,
    setPagination,
    setSort,
    searchAdminroles,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
    setSelected,
    resetFiltersAndPagination,

    changeClassicFilter
} from '../../store/modules/adminrole/adminroleActions';
import AdminroleForm from '../../components/pageModule/Adminroles/AdminroleForm';

const MySwal = withReactContent(Swal);

const Adminroles = ({
    excludedActions = [],
    onSelectedRowsChange = () => { },
    onClickedRow = () => { },
    externalCondition = {},
    selectedIds = []
}) => {

    const adminroleState = useSelector(state => state.adminrole);
    const dispatch = useDispatch();

    useEffect(() => {
        return () => {
            dispatch(resetFiltersAndPagination());
        };
    }, []);

    const handleCreate = async (formData) => {
        try {
            const response = await dispatch(createAdminrole(formData));
            toast.success('Admin Role created successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error creating adminrole', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleUpdate = async (id, formData) => {
        try {
            const response = await dispatch(updateAdminrole(id, formData));
            toast.success('Adminrole updated successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error updating adminrole', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteAdminrole(id));
                toast.success('Adminrole deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return true;
            } catch (error) {
                toast.error(error.message || 'Error deleting adminrole', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return false;
            }
        }
        return false;
    };

    const handleDownload = async (type) => {
        try {
            const response = await dispatch(downloadAdminrole(type));
            window.open(response.url, '_blank')
            return response;
        } catch (error) {
            toast.error(error.message || 'Error downloading adminroles', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    };

    const columns = [
        {
            name: 'Name',
            selector: row => row.name ?? '-'
        },
        {
            name: 'Created At',
            selector: row => row.created_at ?? '-',
            sortField: 'internal_roles.created_at',
            sortable: true,
            wrap: true,
            width: '250px'
        },
    ];


    const actions = {
        fetch: (queryFilter, activeFilter, shouldUseFilter, externalCondition) => dispatch(fetchAdminroles(queryFilter, activeFilter, shouldUseFilter, externalCondition)),
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        download: handleDownload,
        search: (search) => dispatch(searchAdminroles(search)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setSort: (sort) => dispatch(setSort(sort)),
        setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
        changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
        addNewCondition: (rule) => dispatch(addNewCondition(rule)),
        updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
        updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
        removeCondition: (index) => dispatch(removeCondition(index)),
        saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter)),
        setSelected: (selectedRows) => dispatch(setSelected(selectedRows)),

        onClassicFilterChange: (name, value) => dispatch(changeClassicFilter(name, value)),
    };

    const defaultAvailableActions = ['create', 'search', 'actions', 'edit'];
    const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

    return (
        <>
            <GenericPage
                title="Admin Roles"
                moduleName="Adminrole"
                moduleKey="adminroles"
                state={adminroleState}
                actions={actions}
                FormComponent={AdminroleForm}
                columns={columns}
                availableActions={availableActions}
                actionsColumnWidth="150px"
                selectableRows={false}
                onSelectedRowsChange={onSelectedRowsChange}
                onClickedRow={onClickedRow}
                selectedIds={selectedIds}
                externalCondition={externalCondition}
            />
        </>
    );
};

export default Adminroles; 